import React from 'react'
import PropTypes from 'prop-types'
import { Modal, ControlledModal } from '../Modal/Modal'
import Portal from 'components/Portal/Portal'
import Transition from 'react-prop-transition'
import {
  GET_CURRENT_USER_FREQUENT_PAYOR_ACCOUNT,
  GET_FREQUENT_PAYOR_WEBCART_FROM_AMOUNT,
  GET_FREQUENT_PAYOR_STATEMENT,
} from 'requests'
import { PaymentButton } from '../Checkout/Checkout'
import PlusIcon from '@tecuity/sos-icons/react/plus-heavy'
import PencilIcon from '@tecuity/sos-icons/react/pencil'
// import TooltipWrapper from '../TooltipWrapper/TooltipWrapper'
import { DumbField } from '../Wizard2/Field'
import { TextInput } from '../Wizard2/fieldComponents/Form'
import { MASK } from '../Wizard2/validate'
import DownloadIcon from '@tecuity/sos-icons/react/download-box'
import FileDownloader from '../FormManager/FileDownloader'
import sub from 'date-fns/sub'
import format from 'date-fns/format'
import { Select } from '../Wizard2/fieldComponents/Form'
import './FrequentPayor.scss'
import { FormattedAddress } from '../../Utilities/Utilities'
import AddressBlock from 'components/Wizard2/fieldComponents/AddressBlock'
import { VALIDATE } from 'components/Wizard2/validate'
import { UPDATE_FREQUENT_PAYOR_ADDRESS } from '../../requests'
import CheckoutSlider from 'components/Checkout/CheckoutSlider'
import toggleModalObscuredContext from 'components/UserAccountEditor/toggleModalObscuredContext'
import deepEqual from 'deep-equal'
import { history } from '../../store'

export const useFrequentPayor = () => {
  const [payor, setPayor] = React.useState()

  React.useEffect(() => {
    GET_CURRENT_USER_FREQUENT_PAYOR_ACCOUNT((payor) => {
      setPayor({
        ...payor,
        payorType:
          payor.PREPAID_YN && payor.BILLABLE_YN
            ? 'both'
            : payor.PREPAID_YN
            ? 'prepaid'
            : 'billable',
        payorLabel:
          payor.PREPAID_YN && payor.BILLABLE_YN
            ? 'Payment'
            : payor.PREPAID_YN
            ? 'Prepaid'
            : 'Billable',
      })
    })
  }, [])

  const setBalance = (Balance) =>
    setPayor((payor) => ({
      ...payor,
      Balance,
    }))

  const setAddress = (ADDRESS) => {
    setPayor((payor) => ({
      ...payor,
      ADDRESS,
    }))
  }

  return { payor, loading: payor ? false : true, setBalance, setAddress }
}

export const FrequentPayorDataProvider = ({ children }) => {
  const payor = useFrequentPayor()
  return children(payor)
}
FrequentPayorDataProvider.propTypes = {
  children: PropTypes.func.isRequired,
}

export const FrequentPayorCheckoutSection = ({ disabled, error, total, onPaymentRequested }) => {
  const { loading: payorLoading, payor } = useFrequentPayor()

  return (
    <div className="frequent-payor-section">
      <p className="balance">
        Account Balance:{' '}
        {payorLoading ? 'loading' : <strong>{`$${payor.Balance.toFixed(2)}`}</strong>}
      </p>
      <div className="flex-row">
        <PaymentButton
          disabled={disabled || payorLoading || (payor.Balance < total && !payor.BILLABLE_YN)}
          onClick={onPaymentRequested}
          className={`${payorLoading || (payor.Balance < total && !payor.BILLABLE_YN) ? 'visually-disabled' : ''}`}
          style={{ paddingRight: 7, paddingLeft: 8 }}
          forceShowError={error}
        >
          Pay with {payorLoading ? 'Prepaid' : payor.payorLabel} Account
        </PaymentButton>
      </div>
      <div className="flex-row">
        <p className="funds-hint">
          <span className="tip" aria-hidden>
            i
          </span>{' '}
          Funds may be added by visiting "My Account".
        </p>
      </div>
    </div>
  )
}

const getPrepaidWebcart = (amount) =>
  new Promise((resolve, reject) => {
    GET_FREQUENT_PAYOR_WEBCART_FROM_AMOUNT(amount, resolve, reject)
  })

export const FrequentPayorModal = ({
  isOpen: initialIsOpen,
  onModalClosed,
  onRequestModalClose,
  onBalanceChanged,
}) => {
  const { loading: payorLoading, payor, setBalance } = useFrequentPayor()
  const [isOpen, setIsOpen] = React.useState(initialIsOpen)
  const [checkoutOpen, setCheckoutOpen] = React.useState(false)
  const [balanceToUpdate, setBalanceToUpdate] = React.useState(0)
  const [webCart, setWebCart] = React.useState(null)
  const toggleModalObscured = React.useContext(toggleModalObscuredContext)
  const modal = React.useRef()

  React.useEffect(() => {
    if (initialIsOpen && !isOpen) {
      setIsOpen(true)
    } else if (!initialIsOpen && isOpen) {
      modal.current.closeModal(() => {
        setIsOpen(false)
      })
    }
  }, [initialIsOpen])

  return (
    <React.Fragment>
      <Portal isOpened={isOpen}>
        <Modal
          title="Add Funds"
          closeModal={() => {
            setIsOpen(false)
            onModalClosed()
          }}
          modalSize="small"
          closeButton
          autoHeight
          ref={modal}
        >
          <div className="inner-body frequent-payor-modal">
            <FrequentPayor
              onAddFundsRequested={(webCart, newBalance) => {
                onRequestModalClose()
                setBalanceToUpdate(newBalance)
                setWebCart(webCart)
                setCheckoutOpen(true)
                toggleModalObscured(true)
              }}
              payorLoading={payorLoading}
              payor={payor}
              setBalance={setBalance}
            />
          </div>
        </Modal>
      </Portal>
      <CheckoutSlider
        isOpen={checkoutOpen}
        onRequestClose={() => setCheckoutOpen(false)}
        onTransactionComplete={(closeSlider, refNum) => {
          closeSlider()
          setBalance(balanceToUpdate)
          onBalanceChanged(balanceToUpdate)
          toggleModalObscured(false)
          onRequestModalClose()
          history.replace(`/receipt?ref=${refNum || webCart.REF_NUM}`)
        }}
        onCancel={() => {
          toggleModalObscured(false)
        }}
        webCart={webCart}
        skipCartQuantityUpdate
      />
    </React.Fragment>
  )
}

const FrequentPayor = ({ onAddFundsRequested, payor, payorLoading, setBalance }) => {
  const [other, setOther] = React.useState('0')
  const [updatingBalance, setUpdatingBalance] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState("")

  const setOtherAmount = (amount) => {
    let sanitizedAmount = MASK.onlyNumbers(amount, {
      leadingZerosAllowed: false,
      allowDecimal: true,
    })
    let splitAmount = sanitizedAmount.split('.')
    if (splitAmount.length > 1) {
      sanitizedAmount = `${splitAmount[0]}.${splitAmount[1].slice(0, 2)}`
    }
    setOther(sanitizedAmount)
  }

  const addFunds = (amount) => {
    const amt = parseFloat(amount, 10)
    const newBalance = amt + payor.Balance
    setUpdatingBalance(true)

    getPrepaidWebcart(amt)
    .then((webcart) => {
      onAddFundsRequested(webcart, newBalance)
      setUpdatingBalance(false)
    })
    .catch(err => {
      setUpdatingBalance(false)
      setErrorMessage(err.message || "Sorry something went wrong while processing your request.")
      console.error(err)
    })
  }

  return (
    <div className="flex-column frequent-payor">
      <h2 className="flex-column total-header">
        <span className="label">Current account balance:</span>
        <Transition
          props={{ number: payorLoading ? 0 : payor.Balance }}
          duration={700}
          easing="cubic"
        >
          {(props) => <span className="total">${props.number.toFixed(2)}</span>}
        </Transition>
      </h2>
      <p className="instructions">Select an amount or input a custom amount:</p>
      <div className="flex-row payment-options">
        <PaymentOptionButton amount={10} onClick={addFunds} disabled={updatingBalance} />
        <PaymentOptionButton amount={50} onClick={addFunds} disabled={updatingBalance} />
        <PaymentOptionButton amount={100} onClick={addFunds} disabled={updatingBalance} />
        <PaymentOptionButton amount={500} onClick={addFunds} disabled={updatingBalance} />
        <PaymentOptionButton amount={1000} onClick={addFunds} disabled={updatingBalance} />
      </div>
      <div className="flex-row wizard center other-wrapper">
        <DumbField fieldId="other-fpa" label="Custom amount (at least $10)" className="other-field">
          <TextInput fieldId="other-fpa" width="100%" value={other} onChange={setOtherAmount} />
        </DumbField>
        <button
          disabled={
            parseFloat(other, 10) < 10 ||
            !other ||
            updatingBalance ||
            other[other.length - 1] === '.'
          }
          className="btn btn-primary btn-raised add-button"
          onClick={() => addFunds(other)}
        >
          Add Funds
        </button>
      </div>
      <p aria-live="assertive" className="danger-text">
        {errorMessage}
      </p>
    </div>
  )
}
export default FrequentPayor

const PaymentOptionButton = ({ amount, onClick, disabled }) => (
  <button
    className="btn btn-primary btn-raised"
    onClick={() => onClick(amount)}
    disabled={disabled}
  >
    ${amount}
  </button>
)

export const FrequentPayorUserEditorSection = () => {
  const {
    loading: payorLoading,
    payor,
    setBalance,
    setAddress: setPayorAddress,
  } = useFrequentPayor()
  const [addingFunds, setAddingFunds] = React.useState(false)
  const [selectingStatementDate, setSelectingStatementDate] = React.useState(false)
  const [editingAddress, setEditingAddress] = React.useState(false)
  const [address, setAddress] = React.useState({})
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (!payorLoading) {
      setAddress(payor.ADDRESS)
    }
  }, [payorLoading])

  const saveAddress = () => {
    VALIDATE.addressIsValid(address, {}, {}, (isValid, error) => {
      if (isValid === false) {
        setError(error)
      } else {
        let oldAddress = payor.ADDRESS
        if (!deepEqual(address, oldAddress)) {
          setPayorAddress(address)
          UPDATE_FREQUENT_PAYOR_ADDRESS(
            address,
            () => {
              setEditingAddress(false)
            },
            (err) => {
              setError(err.message)
              setPayorAddress(oldAddress)
            }
          )
        } else {
          setEditingAddress(false)
        }
      }
    })
  }

  return (
    <div className="flex-column frequent-payor-user-section">
      <h2>
        {payorLoading ? 'Prepaid' : payor.payorLabel} Account
        <button
          className="btn btn-primary btn-raised"
          onClick={() => setSelectingStatementDate(true)}
        >
          <DownloadIcon alt="Download icon" /> Download Statements
        </button>
      </h2>
      <div className="flex-row account-details">
        <div className="flex-column">
          <label id="fp-acc-name">Account Name:</label>
          <p aria-labelledby="fp-acc-name" aria-busy={payorLoading}>
            <strong>{payorLoading ? 'Loading' : payor.Name}</strong>
          </p>
        </div>
        <div className="flex-column">
          <label id="fp-acc-num">Account Number:</label>
          <p aria-labelledby="fp-acc-num" aria-busy={payorLoading}>
            <strong>{payorLoading ? 'Loading' : payor.Frequent_Payor_Num}</strong>
          </p>
        </div>
      </div>
      <div className="flex-row address-section">
        <div className="flex-column fp-acc-address">
          <label id="fp-acc-address">Account Address:&nbsp;</label>
          <p aria-labelledby="fp-acc-address" aria-busy={payorLoading}>
            <strong>{payorLoading ? 'Loading' : <FormattedAddress value={payor.ADDRESS} />}</strong>
          </p>
        </div>
        <div className="flex-row align-center">
          <button
            className="btn btn-primary btn-raised"
            onClick={() => {
              setAddress(payor.ADDRESS)
              setError('')
              setEditingAddress(true)
            }}
          >
            <div className="center">
              <PencilIcon className="edit-icon" /> Edit Address
            </div>
          </button>
        </div>
      </div>
      <div className="flex-row balance-section">
        <div className="flex-column prepaid-balance">
          <label id="prepaid-balance-label">Account Balance:&nbsp;</label>
          <p aria-labelledby="prepaid-balance-label" aria-busy={payorLoading}>
            <strong>{payorLoading ? 'Loading' : `$${payor.Balance.toFixed(2)}`}</strong>
          </p>
        </div>
        <div className="flex-row align-center">
          <button className="btn btn-primary btn-raised" onClick={() => setAddingFunds(true)}>
            <div className="center">
              <PlusIcon /> Add Funds
            </div>
          </button>
        </div>
      </div>
      <FrequentPayorModal
        isOpen={addingFunds}
        onRequestModalClose={() => setAddingFunds(false)}
        onModalClosed={() => setAddingFunds(false)}
        onBalanceChanged={setBalance}
      />
      <ControlledModal
        title="Prepaid Statements"
        isOpen={selectingStatementDate}
        onModalClosed={() => setSelectingStatementDate(false)}
        onRequestModalClose={() => setSelectingStatementDate(false)}
      >
        <div className="inner-body">
          <StatementSelector onDownloadInitiated={() => setSelectingStatementDate(false)} />
        </div>
      </ControlledModal>
      <ControlledModal
        title="Edit Address"
        isOpen={editingAddress}
        onModalClosed={() => setEditingAddress(false)}
        onRequestModalClose={() => setEditingAddress(false)}
        closeButton={false}
        cancelButton
        saveButton
        save={saveAddress}
        noSaveOnClose={true}
      >
        <form className="inner-body wizard">
          <DumbField errorMessage={[error]}>
            <AddressBlock
              isValid={(error || []).length > 0 ? false : null}
              width="100%"
              value={address}
              onChange={(value) => setAddress(value)}
              showCityStateZip
              allowSelection={false}
              singleColSize="col-12"
              triColSizes={{
                CITY: 'col-5',
                STATE: 'col-3',
                ZIP: 'col-4',
              }}
            />
          </DumbField>
        </form>
      </ControlledModal>
    </div>
  )
}

const StatementSelector = ({ onDownloadInitiated }) => {
  const options = new Array(12).fill(null).map((_, i) => {
    const date = sub(new Date(), { months: i + 1 })
    return {
      value: `${format(date, 'MM')}/01/${format(date, 'Y')}`,
      label: format(date, 'MMMM Y'),
    }
  })
  const [selectedDate, setSelectedDate] = React.useState('')
  const [blob, setBlob] = React.useState(null)
  const [rawBlob, setRawBlob] = React.useState(null)
  const [downloadOnNextTick, setDownloadOnNextTick] = React.useState(false)
  const [serverError, setServerError] = React.useState(null)
  const [downloading, setDownloading] = React.useState(false)
  const downloader = React.useRef()

  React.useEffect(() => {
    if (downloadOnNextTick) {
      downloader.current.download()
      setDownloadOnNextTick(false)
    }
  }, [downloadOnNextTick])

  const downloadStatement = (date) => {
    setDownloading(true)
    GET_FREQUENT_PAYOR_STATEMENT(date, (res) => {
      if (res.ok) {
        res.blob().then((blob) => {
          const blobObject = window.URL.createObjectURL(blob)
          setBlob(blobObject)
          setRawBlob(blob)
          setDownloadOnNextTick(true)
          setServerError(null)
          setDownloading(false)
          onDownloadInitiated()
        })
      } else {
        res.json().then((err) => setServerError(err.message))
        setDownloading(false)
      }
    })
  }

  return (
    <div className="flex-column">
      <div className="statement-selector wizard flex-row align-center">
        <DumbField label="Statement Date" fieldId="statement-select">
          <Select
            options={[
              { value: '', label: '[Select a statement date]', disabled: true },
              ...options,
            ]}
            value={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            fieldId="statement-select"
          />
        </DumbField>
        <button
          disabled={!selectedDate || downloading}
          className="btn btn-primary btn-raised"
          onClick={() => downloadStatement(selectedDate)}
        >
          <DownloadIcon alt="Download icon" /> {downloading ? `Downloading` : `Download`}
        </button>
      </div>
      <p className="danger-text" role="alert" style={{ display: serverError ? 'block' : 'none' }}>
        {serverError || 'Something went wrong'}
      </p>
      <FileDownloader
        ref={downloader}
        href={blob}
        blob={rawBlob}
        fileName={`Account Statement.pdf`}
      />
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/FrequentPayor/FrequentPayor.js